// ─────────────────────────────────────────────────────────────────────────────
// Fondo de un slide del Recap (Wrapped): color/gradiente + imagen PNG opcional (2026-10-16).
// Un solo lugar arma el estilo para que el editor, la vista previa de la edición y el Wrapped del
// colaborador pinten lo mismo.
// ─────────────────────────────────────────────────────────────────────────────
import type { CSSProperties } from 'react';
import { getRecognitionErrorMessage, RecognitionApiError, type RecapSlideDto } from '@/api/recognition';

/** Tope de la imagen de fondo. Igual que `RecapBackgroundValidator.MaxFileSizeBytes` del backend. */
export const FONDO_IMAGEN_MAX_BYTES = 5 * 1024 * 1024;

/** Solo URLs absolutas http(s): el backend rechaza el resto al guardar (no `javascript:` ni `data:`). */
function esUrlHttp(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/** Escapa lo que cerraría el `url("…")` de CSS. Una URL de Blob con SAS no los trae, pero se blinda. */
function cssUrl(url: string): string {
  return `url("${url.replace(/["\\\n\r]/g, (c) => encodeURIComponent(c))}")`;
}

/**
 * Estilo de fondo del slide. Con imagen: la imagen cubre el slide (`cover`, centrada) y el color o
 * gradiente queda debajo como respaldo mientras carga o si falla. Sin imagen: el color de siempre.
 *
 * Siempre las MISMAS propiedades largas, nunca el atajo `background`: si el editor pasara de
 * `{ background }` a `{ backgroundColor, backgroundImage }` al subir o quitar la foto, React avisa del
 * conflicto y puede borrar el color al quitar el atajo. (Además jsdom descarta el `background` de
 * varias capas, así que con propiedades largas las pruebas sí ven la imagen.)
 */
export function estiloDeFondo(slide: Pick<RecapSlideDto, 'fondo' | 'fondoImagenUrl'>): CSSProperties {
  const fondo = slide.fondo?.trim() ?? '';
  const esGradiente = /gradient\(/i.test(fondo);
  const url = slide.fondoImagenUrl?.trim();
  const capas = [
    url && esUrlHttp(url) ? cssUrl(url) : null,
    esGradiente ? fondo : null,
  ].filter((c): c is string => c !== null);

  return {
    backgroundColor: esGradiente || !fondo ? 'transparent' : fondo,
    backgroundImage: capas.length > 0 ? capas.join(', ') : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
}

/**
 * Validación previa a subir (el backend vuelve a validar por la firma real del archivo). Devuelve la
 * clave i18n del error o `null` si el archivo sirve.
 */
export function errorDeFondoImagen(file: File): string | null {
  const esPng = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
  if (!esPng) return 'admin.recap.photoErrPng';
  if (file.size > FONDO_IMAGEN_MAX_BYTES) return 'admin.recap.photoErrSize';
  return null;
}

/** Mensaje de error ya resuelto: clave i18n y sus valores. */
export interface ErrorDeFondo {
  clave: string;
  valores?: Record<string, string | number>;
}

const FIRMAS: readonly { formato: string; bytes: readonly number[]; desde?: number }[] = [
  { formato: 'PNG', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
  { formato: 'JPG', bytes: [0xff, 0xd8, 0xff] },
  { formato: 'GIF', bytes: [0x47, 0x49, 0x46, 0x38] },
  { formato: 'WEBP', bytes: [0x57, 0x45, 0x42, 0x50], desde: 8 },
];

/** Los primeros `n` bytes del archivo. Con FileReader y no `Blob.arrayBuffer()`, que jsdom no trae. */
function leerCabecera(file: File, n: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(new Uint8Array(lector.result as ArrayBuffer));
    lector.onerror = () => reject(lector.error ?? new Error('No se pudo leer el archivo.'));
    lector.readAsArrayBuffer(file.slice(0, n));
  });
}

/** Formato real por los primeros bytes del archivo, no por su extensión. `null` = desconocido. */
async function formatoReal(file: File): Promise<string | null> {
  const cabecera = await leerCabecera(file, 12);
  const firma = FIRMAS.find(({ bytes, desde = 0 }) => bytes.every((b, i) => cabecera[desde + i] === b));
  return firma?.formato ?? null;
}

/**
 * 2026-10-20 — además de extensión y tamaño, revisa el CONTENIDO: una imagen descargada de internet con
 * extensión .png suele ser en realidad JPG, WEBP o GIF, y el servidor la rechazaba con un 422 que la
 * pantalla mostraba como «No se pudo subir la imagen». Así se dice qué formato es, antes de subir.
 */
export async function validarFondoImagen(file: File): Promise<ErrorDeFondo | null> {
  const porNombre = errorDeFondoImagen(file);
  if (porNombre) return { clave: porNombre };
  const formato = await formatoReal(file);
  if (formato === 'PNG') return null;
  return formato
    ? { clave: 'admin.recap.photoErrNotPng', valores: { formato } }
    : { clave: 'admin.recap.photoErrPng' };
}

/**
 * Motivo de una subida fallida, para no mostrar siempre el mismo mensaje genérico: lo que rechazó el
 * servidor (422), un archivo demasiado grande para algún tramo de la red (413), el endpoint que aún no
 * existe en el ambiente (404) o la falta de permisos (401/403). Otro código se muestra para soporte.
 */
export function errorDeSubidaDeFondo(error: unknown): ErrorDeFondo {
  if (!(error instanceof RecognitionApiError) || error.status <= 0) return { clave: 'admin.recap.photoErrUpload' };
  if (error.status === 422) {
    return { clave: 'admin.recap.photoErrRejected', valores: { motivo: getRecognitionErrorMessage(error, '') } };
  }
  if (error.status === 413) return { clave: 'admin.recap.photoErrSize' };
  if (error.status === 404) return { clave: 'admin.recap.photoErrUnavailable' };
  if (error.status === 401 || error.status === 403) return { clave: 'admin.recap.photoErrForbidden' };
  return { clave: 'admin.recap.photoErrUploadCode', valores: { codigo: error.status } };
}
